// ============================================================================
// Morning Briefing — pure model (CLB, Orders alerts → one daily briefing)
// ============================================================================
// Takes the unread Orders notification rows and turns them into what the briefing shows:
// items, groups, the "needs you today" verdict, the top-3 focus and the week strip.
// No React, no network, no clock of its own: `now` is an argument, so tests are exact.
// ============================================================================

export const LATE_HARD_DAYS = 21;               // late this long = needs you today
const DAY = 86400000;

/** A local calendar day as 'YYYY-MM-DD'. */
export function dayKey(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
const toUtc = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || '')); return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : null; };
/** Whole days from `iso` to today (positive = the date is in the past). */
export function daysSince(iso, now) {
  const a = toUtc(iso); const t = toUtc(dayKey(now));
  return a == null ? null : Math.round((t - a) / DAY);
}
export function fmtDay(iso) {
  const u = toUtc(iso); if (u == null) return '';
  const d = new Date(u);
  return `${d.getUTCDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()]}`;
}

/** Orders notification = its type is filed under the Orders portal (or its key starts with orders.). */
export function isOrdersRow(row) {
  const n = row && row.notifications;
  if (!n) return false;
  const portal = n.notification_types && n.notification_types.portal;
  return portal === 'orders' || String(n.type_key || '').startsWith('orders.');
}

const KIND = { pps: 'PPs', pp: 'PPs', bulk: 'Bulk', sample: 'Sample' };
const KIND_LABEL = { pps: 'PP samples', pp: 'PP samples', bulk: 'Bulk', sample: 'Sample' };
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** 'Thu 29 Oct' */
export function fmtDow(iso) {
  const u = toUtc(iso); if (u == null) return '';
  return `${DOW[new Date(u).getUTCDay()]} ${fmtDay(iso)}`;
}
/** Rows about no single model (their payload has none): the model column shows this plain word instead. */
const NOUN = { 'orders.sheet_chase': 'Sheet', 'orders.sheet_returned': 'Sheet', 'orders.query_open': 'Query', 'orders.comment_mention': 'Comment', 'orders.import_failed': 'Import', 'orders.pp_arrived_unanswered': 'PP samples', 'orders.pp_due_soon': 'PP samples' };
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
const ATTENTION = new Set(['orders.sheet_chase', 'orders.import_failed', 'orders.pp_arrived_unanswered', 'orders.query_open']);
const MENTIONS = new Set(['orders.comment_mention', 'orders.sheet_returned']);

/** The pill (what) and the line (when / next step) of a note row — short, and never repeating the PI, model or supplier columns. */
function shortCopy(key, p, supplier) {
  const days = Number(p.since_days != null ? p.since_days : p.days);
  switch (key) {
    case 'orders.sheet_chase': return { lead: 'Sheet not back', line: Number.isFinite(days) && days > 0 ? `${plural(days, 'day')}${supplier ? ` with ${supplier}` : ''}` : 'Chase the supplier' };
    case 'orders.query_open': return { lead: 'Query waiting', line: 'No answer yet' };
    case 'orders.sheet_returned': return { lead: 'Sheet came back', line: 'Take a look' };
    case 'orders.comment_mention': return { lead: 'You were mentioned', line: p.col_label ? `In ${p.col_label}` : 'Open to read it' };
    case 'orders.pp_arrived_unanswered': return { lead: 'Samples arrived', line: 'Say yes or no' };
    case 'orders.import_failed': return { lead: 'File did not load', line: 'Try again' };
    default: return {};
  }
}

/** One notification row → one briefing item. */
export function toItem(row, now) {
  const n = row.notifications; const p = n.payload || {};
  const key = n.type_key;
  const item = {
    id: row.id, rowId: row.id, n, key, pi: p.pi_no || '', model: p.model || '', supplier: p.supplier_code || '',
    kind: p.kind || '', dueOn: p.due_on || p.trigger_on || null, days: 0, group: 'fyi', needs: false, score: 0,
    title: n.title || '', text: n.body || '',
    // What the row shows: the pill says WHAT it is, the line under it says WHEN or the one next step (never the same thing twice).
    lead: n.title || '', line: n.body || '', kindLabel: '', noun: NOUN[key] || 'Note',
  };
  const kindWord = KIND[String(p.kind || '').toLowerCase()] || 'Delivery';
  item.kindLabel = KIND_LABEL[String(p.kind || '').toLowerCase()] || (key === 'orders.pp_due_soon' ? 'PP samples' : key.startsWith('orders.delivery') ? 'Delivery' : '');
  if (key === 'orders.delivery_overdue') {
    const live = p.due_on ? daysSince(p.due_on, now) : null;
    item.days = Math.max(1, live != null ? live : Number(p.since_days) || 1);
    item.group = 'late';
    item.needs = item.days >= LATE_HARD_DAYS;
    item.score = 50 + Math.min(item.days, 60);
    item.text = `${kindWord} delivery${item.supplier ? ` from ${item.supplier}` : ''} · was due ${fmtDay(p.due_on)} · nothing marked as arrived`;
    item.lead = `Late ${plural(item.days, 'day')}`;
    item.line = p.due_on ? `Promised ${fmtDay(p.due_on)}` : 'Nothing marked as arrived';
  } else if (key === 'orders.delivery_due') {
    const ahead = p.due_on ? -daysSince(p.due_on, now) : null;
    item.days = ahead == null ? 0 : ahead;
    item.group = ahead != null && ahead <= 0 ? 'today' : 'soon';
    item.needs = item.group === 'today';
    item.score = item.needs ? 80 : 20;
    item.text = `${kindWord} delivery${item.supplier ? ` from ${item.supplier}` : ''} · ${ahead != null && ahead < 0 ? `was due ${fmtDay(p.due_on)}` : item.needs ? 'due today' : `due ${fmtDay(p.due_on)}`}`;
    if (item.needs) { item.lead = ahead != null && ahead < 0 ? `Was due ${fmtDay(p.due_on)}` : 'Due today'; item.line = 'Mark it on arrival'; }
    else { item.lead = ahead == null ? 'Coming up' : ahead === 1 ? 'Tomorrow' : `In ${ahead} days`; item.line = p.due_on ? `Due ${fmtDow(p.due_on)}` : ''; }
  } else if (key === 'orders.pp_due_soon') {
    const ahead = p.trigger_on ? -daysSince(p.trigger_on, now) : null;
    item.days = ahead == null ? 0 : ahead;
    item.group = ahead != null && ahead <= 0 ? 'today' : 'soon';
    item.needs = item.group === 'today';
    item.score = item.needs ? 80 : 20;
    item.text = `A PP ${ahead != null && ahead < 0 ? 'was due' : 'is due'}${p.trigger_on ? ` on ${fmtDay(p.trigger_on)}` : ''}${item.supplier ? ` · ${item.supplier}` : ''}`;
    if (item.needs) { item.lead = ahead != null && ahead < 0 ? `PP was due ${fmtDay(p.trigger_on)}` : 'PP due today'; item.line = 'Mark it on arrival'; }
    else { item.lead = ahead == null ? 'Coming up' : ahead === 1 ? 'Tomorrow' : `In ${ahead} days`; item.line = p.trigger_on ? `PP due ${fmtDow(p.trigger_on)}` : ''; }
  } else if (ATTENTION.has(key)) {
    item.group = 'attention'; item.needs = true; item.score = 100; item.text = n.body || n.title || '';
    Object.assign(item, shortCopy(key, p, item.supplier));
  } else if (MENTIONS.has(key)) {
    item.group = 'mentions'; item.score = 30;
    Object.assign(item, shortCopy(key, p, item.supplier));
  }
  return item;
}

export const SECTIONS = [
  { k: 'late', label: 'Late deliveries' },
  { k: 'today', label: 'Due today' },
  { k: 'attention', label: 'Needs your attention' },
  { k: 'mentions', label: 'Comments and replies' },
  { k: 'soon', label: 'Coming up' },
  { k: 'fyi', label: 'Quiet changes' },
];

/** Monday of the week of `now`, as a local Date at midnight. */
const monday = (now) => { const d = new Date(now.getFullYear(), now.getMonth(), now.getDate()); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d; };

/** The whole briefing from the rows. `handled` = ids the person has dealt with (they stay listed, counted). */
export function buildBriefing(rows, now, handled = new Set()) {
  const items = (rows || []).filter(isOrdersRow).map((r) => toItem(r, now));
  const open = items.filter((i) => !handled.has(i.id));
  const needs = open.filter((i) => i.needs);
  const wait = open.filter((i) => !i.needs && i.group !== 'fyi');
  const fyi = open.filter((i) => i.group === 'fyi');
  const sections = SECTIONS.map((s) => {
    const list = items.filter((i) => i.group === s.k).sort(s.k === 'soon' ? (a, b) => a.days - b.days : (a, b) => b.score - a.score || b.days - a.days);
    return { ...s, items: list, open: list.filter((i) => !handled.has(i.id)).length, bySupplier: s.k === 'late' ? groupBySupplier(list) : null };
  }).filter((s) => s.items.length);
  const focus = needs.slice().sort((a, b) => b.score - a.score || b.days - a.days).slice(0, 3);
  const actionable = items.filter((i) => i.group !== 'fyi');
  return {
    items, sections, focus,
    counts: { needs: needs.length, wait: wait.length, fyi: fyi.length },
    total: actionable.length, done: actionable.filter((i) => handled.has(i.id)).length,
    week: weekStrip(items, now, handled),
    empty: items.length === 0,
  };
}

function groupBySupplier(list) {
  const map = new Map();
  list.forEach((i) => { const k = i.supplier || 'Other'; if (!map.has(k)) map.set(k, []); map.get(k).push(i); });
  return [...map.entries()].map(([supplier, items]) => ({ supplier, items, worst: Math.max(...items.map((i) => i.days)) }))
    .sort((a, b) => b.worst - a.worst);
}

/** Late column + Mon..Sun counts, by due date. */
export function weekStrip(items, now, handled = new Set()) {
  const start = monday(now); const today = dayKey(now);
  const days = [...Array(7)].map((_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return { key: dayKey(d), date: d.getDate(), name: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i], today: dayKey(d) === today, n: 0 }; });
  let late = 0;
  items.forEach((i) => {
    if (handled.has(i.id)) return;
    if (i.group === 'late') { late += 1; return; }
    const d = days.find((x) => x.key === i.dueOn); if (d) d.n += 1;
  });
  return { late, days };
}

export function greeting(now, name) {
  const h = now.getHours();
  const word = h < 5 ? 'Good evening' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  return name ? `${word}, ${name}.` : `${word}.`;
}

/** Verdict line: the one sentence at the top. */
export function verdict(counts) {
  if (!counts.needs) return 'Nothing needs you today';
  return `${counts.needs} thing${counts.needs === 1 ? '' : 's'} need${counts.needs === 1 ? 's' : ''} you today`;
}
